"use client";

import { useState, useCallback } from "react";
import { signInWithGoogle } from "@/auth";

type LoginState = "idle" | "loading" | "error";

/**
 * Inline login screen for the editor.
 * Runs Google OAuth popup on the editor origin so the Firebase session
 * persists in this origin's IndexedDB (no cross-origin redirect needed).
 */
export function LoginScreen() {
	const [state, setState] = useState<LoginState>("idle");
	const [errorMessage, setErrorMessage] = useState<string | null>(null);

	const handleSignIn = useCallback(async () => {
		if (state === "loading") return;
		setState("loading");
		setErrorMessage(null);
		try {
			await signInWithGoogle();
			// onAuthStateChanged in useAuth will pick up the new session
		} catch (err) {
			setState("error");
			setErrorMessage(
				err instanceof Error ? err.message : "Sign-in failed. Use your company Google account.",
			);
		}
	}, [state]);

	return (
		<div
			style={{
				minHeight: "100vh",
				background: "#0B0B0C",
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
				justifyContent: "center",
				padding: 24,
			}}
		>
			{/* Logo */}
			<div style={{ marginBottom: 12 }}>
				<span
					style={{
						fontFamily: "'Alternate Gothic ATF', sans-serif",
						fontSize: 32,
						fontWeight: 400,
						color: "#F5F5F7",
						letterSpacing: "-0.01em",
					}}
				>
					Pieter Studio
				</span>
			</div>

			<p
				style={{
					fontFamily: "'Fixture', sans-serif",
					fontSize: 14,
					color: "#7A7570",
					marginBottom: 48,
					textAlign: "center",
				}}
			>
				Video editor -- sign in to continue
			</p>

			{/* Error banner */}
			{state === "error" && errorMessage && (
				<div
					style={{
						width: "100%",
						maxWidth: 360,
						background: "#161618",
						border: "1px solid #C0392B",
						padding: "14px 18px",
						marginBottom: 14,
						borderRadius: 2,
					}}
				>
					<div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
						<svg width={14} height={14} viewBox="0 0 14 14" fill="none" stroke="#C0392B" strokeWidth="1.5">
							<path d="M7 1L13 12H1L7 1z" />
							<path d="M7 5v4M7 10.5v.5" />
						</svg>
						<span
							style={{
								fontFamily: "'Fixture', sans-serif",
								fontSize: 11,
								color: "#C0392B",
								letterSpacing: "0.08em",
							}}
						>
							SIGN-IN FAILED
						</span>
					</div>
					<p
						style={{
							fontFamily: "'Fixture', sans-serif",
							fontSize: 13,
							color: "#7A7570",
							margin: 0,
							lineHeight: 1.55,
						}}
					>
						{errorMessage}
					</p>
				</div>
			)}

			{/* OAuth button */}
			<button
				onClick={handleSignIn}
				disabled={state === "loading"}
				style={{
					width: "100%",
					maxWidth: 360,
					background: "#161618",
					border: "1px solid #2A2A2A",
					padding: "13px 20px",
					display: "flex",
					alignItems: "center",
					gap: 12,
					cursor: state === "loading" ? "not-allowed" : "pointer",
					transition: "border-color 0.1s",
					marginBottom: 10,
					borderRadius: 2,
				}}
				onMouseEnter={(e) => {
					if (state !== "loading") e.currentTarget.style.borderColor = "#C9A84C";
				}}
				onMouseLeave={(e) => {
					e.currentTarget.style.borderColor = "#2A2A2A";
				}}
			>
				{state === "loading" ? (
					<>
						<div
							style={{
								width: 18,
								height: 18,
								flexShrink: 0,
								border: "1.5px solid #3A3A3A",
								borderTopColor: "#C9A84C",
								borderRadius: "50%",
								animation: "ps-login-spin 0.8s linear infinite",
							}}
						/>
						<span
							style={{
								fontFamily: "'Fixture', sans-serif",
								fontSize: 15,
								color: "#7A7570",
							}}
						>
							Opening Google...
						</span>
					</>
				) : (
					<>
						<GoogleIcon />
						<span
							style={{
								fontFamily: "'Fixture', sans-serif",
								fontSize: 15,
								color: "#F5F5F7",
							}}
						>
							{state === "error" ? "Try another account" : "Continue with Google"}
						</span>
					</>
				)}
			</button>

			{state === "loading" && (
				<div
					style={{
						fontFamily: "'Fixture', sans-serif",
						fontSize: 11,
						color: "#3A3A3A",
						marginBottom: 12,
						textAlign: "center",
					}}
				>
					Popup window opened
				</div>
			)}

			{/* Domain restriction */}
			<p
				style={{
					fontFamily: "'Fixture', sans-serif",
					fontSize: 12,
					color: "#3A3A3A",
					textAlign: "center",
					maxWidth: 360,
					lineHeight: 1.6,
					marginBottom: 0,
				}}
			>
				Workspace SSO required
			</p>

			{/* Version */}
			<div
				style={{
					position: "fixed",
					bottom: 24,
					fontFamily: "'Alternate Gothic ATF', sans-serif",
					fontSize: 11,
					fontWeight: 400,
					color: "#3A3A3A",
					letterSpacing: "0.08em",
				}}
			>
				V0.1 · PIETER STUDIO
			</div>

			{/* Spinner animation */}
			<style>{`
				@keyframes ps-login-spin {
					to { transform: rotate(360deg); }
				}
			`}</style>
		</div>
	);
}

function GoogleIcon() {
	return (
		<svg width={18} height={18} viewBox="0 0 48 48">
			<path
				fill="#EA4335"
				d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
			/>
			<path
				fill="#4285F4"
				d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
			/>
			<path
				fill="#FBBC05"
				d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
			/>
			<path
				fill="#34A853"
				d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
			/>
			<path fill="none" d="M0 0h48v48H0z" />
		</svg>
	);
}
